import React from 'react';
import { Link } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import { FaMapMarkerAlt, FaMountain, FaUmbrellaBeach, FaTree, FaArrowRight } from 'react-icons/fa';
import destinationsData from '../data/destinations.json';

const Destinations = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const iconMap = {
    'FaUmbrellaBeach': <FaUmbrellaBeach />,
    'FaMountain': <FaMountain />,
    'FaMapMarkerAlt': <FaMapMarkerAlt />,
    'FaTree': <FaTree />,
  };

  return (
    <section id="destinations" className="py-20 bg-gray-50 overflow-hidden">
      <div className="container-custom">
        {/* Header */}
        <div
          ref={ref}
          className={`text-center mb-16 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
        >
          <span className="text-accent-600 font-semibold text-sm uppercase tracking-wide">
            Destinations
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-primary-800 mt-4 mb-6">
            Explore India's Most
            <span className="text-accent-600"> Stunning Landscapes</span>
          </h2>
          <p className="text-xl text-primary-600 max-w-3xl mx-auto">
            From beaches to mountains, deserts to jungles - your caravan can take you anywhere
          </p>
        </div>

        {/* Destinations Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {destinationsData.map((destination, index) => (
            <div
              key={destination.id}
              style={{ transitionDelay: `${index * 150}ms` }}
              className={`group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all hover:-translate-y-3 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
            >
              {/* Image */}
              <div className="relative h-56 overflow-hidden">
                <img 
                  src={destination.image}
                  alt={destination.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                <div className="absolute bottom-4 left-4 text-white">
                  <div className="text-5xl mb-2 opacity-90">
                    {iconMap[destination.icon]}
                  </div>
                  <p className="text-white/90 text-sm">
                    {destination.description}
                  </p>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-2xl font-bold text-primary-800 mb-2">
                  {destination.name}
                </h3>
                <p className="text-primary-600 text-sm mb-4">
                  {destination.description}
                </p>
                
                <div className="flex items-center justify-between mb-4 text-sm">
                  <span className="text-accent-600 font-semibold">
                    {destination.duration}
                  </span>
                  <span className="text-primary-500">
                    Recommended
                  </span>
                </div>

                <h4 className="font-bold text-primary-800 mb-3">Highlights:</h4>
                <ul className="space-y-2 mb-4">
                  {destination.highlights.slice(0, 3).map((highlight, hIndex) => (
                    <li key={hIndex} className="flex items-center space-x-2 text-primary-600 text-sm">
                      <div className="w-1.5 h-1.5 bg-accent-500 rounded-full" />
                      <span>{highlight}</span>
                    </li>
                  ))}
                </ul>

                <Link 
                  to={`/destination/${destination.id}`}
                  className="w-full mt-6 py-3 bg-primary-100 text-primary-700 font-semibold rounded-full hover:bg-primary-200 transition-colors flex items-center justify-center gap-2"
                >
                  View Full Itinerary
                  <FaArrowRight />
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Custom Destinations CTA */}
        <div
          className={`mt-16 bg-gradient-to-r from-accent-500 to-accent-600 rounded-3xl p-12 text-center text-white relative overflow-hidden transition-all duration-700 delay-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
        >
          <div className="absolute inset-0 bg-hero-pattern opacity-10" />
          <div className="relative z-10">
            <h3 className="text-3xl md:text-4xl font-bold mb-4">
              Have a Different Destination in Mind?
            </h3>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              We customize routes across all of India. Tell us where you want to go, 
              and we'll plan the perfect caravan journey for you.
            </p>
            <a
              href="#contact"
              className="inline-block px-8 py-4 bg-white text-accent-600 font-semibold rounded-full hover:bg-primary-50 shadow-lg transition-all hover:scale-105"
            >
              Contact Us for Custom Routes
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Destinations;